import { NavLink, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth, homePathFor } from "@/lib/auth";
import { ListChecks, Folder, Table, Gauge, Envelope, GearSix, SignOut, ArrowLeft, Plus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ModuleSwitcher } from "@/components/ModuleSwitcher";
import { TrakLogo } from "@/components/TrakLogo";
import { Breadcrumbs, BreadcrumbProvider } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { TaskDrawer } from "@/components/TaskDrawer";
import { NewTaskDialog, TaskBell } from "@/pages/Tasks";

export const isTaskStaff = (u) => u && !["end_user", "soc_viewer"].includes(u.role);

export default function TasksLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [params, setParams] = useSearchParams();
  const taskId = params.get("task");
  const staff = isTaskStaff(user);
  const openTask = (id) => { const p = new URLSearchParams(params); p.set("task", id); setParams(p); };
  const closeTask = () => { const p = new URLSearchParams(params); p.delete("task"); setParams(p); };
  const refresh = () => setTick((t) => t + 1);

  const links = [
    { to: "/tasks", label: "My Tasks", icon: ListChecks, exact: true, testid: "tasks-nav-my-tasks", show: staff },
    { to: "/tasks/projects", label: "Projects", icon: Folder, testid: "tasks-nav-projects", show: staff },
    { to: "/tasks/all", label: "All Tasks", icon: Table, testid: "tasks-nav-all-tasks", show: staff },
    { to: "/tasks/dashboard", label: "Dashboard", icon: Gauge, testid: "tasks-nav-dashboard", show: staff },
    { to: "/tasks/request", label: "Request Form", icon: Envelope, testid: "tasks-nav-request-form", show: true },
    { to: "/tasks/settings", label: "Settings", icon: GearSix, testid: "tasks-nav-settings", show: user?.role === "admin" },
  ].filter((l) => l.show);

  const linkClass = ({ isActive }) => cn(
    "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 border-transparent",
    "text-sidebar-muted hover:text-white hover:bg-white/5",
    "dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40",
    isActive && "text-white bg-white/10 border-white dark:text-foreground dark:bg-primary-tint dark:border-primary"
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="tasks-module">
      <aside className="w-60 shrink-0 border-r flex flex-col bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
        <div className="h-14 px-5 border-b border-sidebar-border dark:border-border flex items-center gap-2">
          <button data-testid="tasks-home-logo" onClick={() => nav(homePathFor(user))} title="Back to start" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
            <div className="h-7 w-7 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm"><TrakLogo size={18} /></div>
            <div className="font-display font-bold tracking-tight">TRAK</div>
          </button>
          <span className="ml-auto text-[10px] font-mono text-sidebar-muted dark:text-muted-foreground uppercase">Tasks</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto" data-testid="tasks-nav">
          <button data-testid="tasks-nav-back" onClick={() => nav(-1)} className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-sidebar-muted hover:text-white hover:bg-white/5 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40 transition-colors border-l-2 border-transparent">
            <ArrowLeft size={16} weight="regular" /> Back
          </button>
          {links.map(({ to, label, icon: Icon, exact, testid }) => (
            <NavLink key={to} to={to} end={exact} data-testid={testid} className={linkClass}><Icon size={16} weight="regular" /> {label}</NavLink>
          ))}
        </nav>
        <div className="border-t p-4 border-sidebar-border dark:border-border">
          <div className="text-xs font-mono text-sidebar-muted dark:text-muted-foreground">SIGNED IN AS</div>
          <div className="text-sm mt-1">{user?.name}</div>
          <div className="text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground">{user?.email}</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-sidebar-border dark:border-border">{user?.role}</span>
            <button data-testid="tasks-logout-button" onClick={async () => { await logout(); nav("/login", { replace: true }); }} className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary" title="Sign out"><SignOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <BreadcrumbProvider>
          <header className="h-14 border-b flex items-center px-6 gap-4 bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
            <Breadcrumbs />
            <div className="ml-auto flex items-center gap-3">
              <TaskBell openTask={openTask} />
              {staff && <Button data-testid="new-task-btn" size="sm" onClick={() => setNewOpen(true)} className="rounded-sm h-8 font-mono text-[11px] uppercase bg-white text-sidebar hover:bg-white/90 dark:bg-primary dark:text-primary-foreground"><Plus size={14} className="mr-1" /> New Task</Button>}
              <ModuleSwitcher />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            <div className="p-6">
              <Outlet context={{ openTask, tick, refresh }} />
            </div>
          </main>
        </BreadcrumbProvider>
      </div>

      <NewTaskDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={(t) => { refresh(); openTask(t.id); }} />
      <TaskDrawer taskId={taskId} onClose={closeTask} onChanged={refresh} />
    </div>
  );
}
